import { ResizeGroup } from "@fluentui/react";
import React, { useEffect } from "react";
import { MenuIconButton, MenuIconButtonProps } from "../../../components/Buttons/MenuIconButton/MenuIconButton";
import { ListItemCanvas, ListItemCanvasProps } from "../../../components/Lists/ListItems/shared/ListItemCanvas";
import { GroupedListItemBaseProps } from "../../../components/Lists/shared/props";
import { Media } from "../../../components/Media/Media";
import { MediaImage } from "../../../components/Media/Media.types";
import { Stack, StackItem } from "../../../components/Stacks/Stack/Stack";
import { Text } from "../../../components/Text/Text";
import { useComponentLogger } from "../../../shared/logging";
import { validateProps } from "../../../shared/props";
import { warnDeprecation } from "../../../shared/warnDeprecation";

export interface ListItemMediaWithDetailsProps extends ListItemCanvasProps {
    readonly title: string;
    readonly metadata?: readonly string[];
    readonly menuIconButtonProps?: MenuIconButtonProps;
    readonly icon?: MediaImage;
    readonly devicesOverflowString?: string;
    readonly wrapWithListCanvas?: boolean;
}

interface ResizeGroupData {
    readonly primary: readonly string[];
    readonly overflow: readonly string[];
}

export interface GroupedListItemMediaWithDetailsProps extends ListItemMediaWithDetailsProps, GroupedListItemBaseProps { }

/** @deprecated Please use the new ListItem/MobileListItem with ListItemColumns instead. */
export const ListContentWithDetails: React.FC<ListItemMediaWithDetailsProps | GroupedListItemMediaWithDetailsProps> = ({
    title,
    metadata,
    icon,
    menuIconButtonProps,
    devicesOverflowString,
    menuDisabled,
    wrapWithListCanvas,
    ...otherProps
}) => {
    useComponentLogger("ListContentWithDetails", otherProps.id);

    validateProps("ListContentWithDetails", otherProps);

    useEffect(() => {
        warnDeprecation("ListContentWithDetails has been deprecated, please use the new ListItem/MobileListItem with ListItemColumns instead.");
    }, []);

    const generatedData = {
        primary: metadata,
        overflow: [] as any[],
    };

    const onReduceData = (currentData: ResizeGroupData) => {
        if (currentData.primary.length === 0) {
            return undefined;
        }
        const overflow = [...currentData.primary.slice(-1), ...currentData.overflow];
        const primary = currentData.primary.slice(0, -1);
        return { primary, overflow };
    };

    const onRenderData = (data: ResizeGroupData) => {
        const overFlowText = data.overflow.length > 0 ? data.primary.length > 0 ? `, +${data.overflow.length} ${devicesOverflowString}` : `${data.overflow.length} ${devicesOverflowString}` : "";
        return (
            <>
                <Text variant="metadataText">{data.primary.join(", ")}</Text>
                <Text variant="metadataText">{overFlowText}</Text>
            </>
        );
    };

    const textBlocks = (
        <>
            <Text block={true} variant="headerText">{title}</Text>
            {metadata && (
                <ResizeGroup
                    data={generatedData}
                    onReduceData={onReduceData}
                    onRenderData={onRenderData}
                />
            )}
        </>
    );

    const content = (
        <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="center">
            <StackItem grow={true} >
                {icon ? (
                    <Media size="sm" image={icon} padding="0 6px">
                        {textBlocks}
                    </Media>
                ) : textBlocks}
            </StackItem>
            <StackItem padding="0 4px 0 0">
                {menuIconButtonProps && (
                    <MenuIconButton
                        {...menuIconButtonProps}
                        disabled={menuDisabled}
                        backgroundColor="neutralHigh"
                        iconColor="neutralPrimary"
                        isBeakVisible={false}
                    />
                )}
            </StackItem>
        </Stack>
    );

    return wrapWithListCanvas ? (
        <ListItemCanvas {...otherProps}>
            {content}
        </ListItemCanvas>
    ) : content;
};
